import { APIConfiguration } from '@savgroup-front-common/configuration';
import { computeInternalId } from '@savgroup-front-common/core/helpers/AdditionalInformations/computeInternalId';
import { isFileType } from '@savgroup-front-common/core/helpers/AdditionalInformations/isFileType';
import { ADDITIONAL_INFORMATION_TYPES } from '@savgroup-front-common/types';
import {
  AdditionalInformation,
  AdditionalInformationExtended,
  Reason,
  RELATED_TO,
} from '@/types';

interface ReasonAdapterArgs {
  reasonSelected?: Reason;
  claimIds: string[];
}

export const buildFileUploadEndpoints = ({
  claimIds,
  id,
}: {
  claimIds: string[];
  id: string;
}): Record<string, { uploadEndpoint: string; deleteEndpoint: string }> => {
  return claimIds.reduce((acc, claimId) => {
    return {
      ...acc,
      [claimId]: {
        uploadEndpoint: `${APIConfiguration.claim}claims/${claimId}/additionalInformation/${id}/file`,
        deleteEndpoint: `${APIConfiguration.claim}claims/${claimId}/additionalInformation/${id}/file`,
      },
    };
  }, {});
};

export const adaptReason = ({ reasonSelected }: ReasonAdapterArgs) => {
  const newNeededInformation = reasonSelected?.neededInformation.reduce<
    AdditionalInformationExtended[]
  >((acc, curr) => {
    const { uniquenessByProduct, id, claimIds } = curr;

    if (uniquenessByProduct) {
      return [
        ...acc,
        ...claimIds.map((claimId) => {
          const internalId = computeInternalId({
            id,
            claimId,
            uniquenessByProduct: true,
          });

          return {
            ...curr,
            internalId,
            claimIds: [claimId],
            fileUploadEndpoints: isFileType(curr)
              ? buildFileUploadEndpoints({
                  claimIds,
                  id,
                })
              : undefined,
          };
        }),
      ];
    }

    const internalId = computeInternalId({
      id,
      uniquenessByProduct: false,
    });

    return [
      ...acc,
      {
        ...curr,
        claimIds,
        internalId,
        fileUploadEndpoints: isFileType(curr)
          ? buildFileUploadEndpoints({
              claimIds,
              id,
            })
          : undefined,
      },
    ];
  }, []);

  return {
    ...reasonSelected,
    neededInformation: newNeededInformation,
  };
};

export const prepareReasonAdditionalInformation = (
  claimId: string,
  reasonAdditionalInformation: Record<string, { value: string }>,
  neededInformations: AdditionalInformation[],
): unknown => {
  const neededInformationIds =
    neededInformations?.map((neededInformation) => neededInformation.id) ?? [];

  const neededInformationsDidactics = neededInformations
    ?.filter(
      (neededInformation) =>
        neededInformation.type ===
          ADDITIONAL_INFORMATION_TYPES.NOTIFICATION_INFO ||
        neededInformation.type ===
          ADDITIONAL_INFORMATION_TYPES.NOTIFICATION_ALERT,
    )
    .map((neededInformation) => ({
      id: neededInformation.id,
      value: neededInformation.name,
    }));

  const neededInformationsFormated = Object.keys(
    reasonAdditionalInformation || {},
  )
    .filter((key) => {
      const needInformationKey = key.endsWith(claimId)
        ? key.replace(`_${claimId}`, '')
        : key;

      return neededInformationIds.includes(needInformationKey);
    })
    .map((key: any) => {
      const needInformationKey = key.endsWith(claimId)
        ? key.replace(`_${claimId}`, '')
        : key;

      const neededInformation = neededInformations?.find(
        (item) => item.id === needInformationKey,
      );

      if (isFileType(neededInformation)) {
        // On ne peut pas passer un File ou un File[] dans `value` :
        // le payload est sérialisé en JSON et le backend crash. Les
        // fichiers sont uploadés à part via leur endpoint dédié.
        return {
          id: needInformationKey,
          value: '',
        };
      }

      if (
        reasonAdditionalInformation &&
        reasonAdditionalInformation[key] &&
        reasonAdditionalInformation[key]?.value
      ) {
        return {
          id: needInformationKey,
          value: reasonAdditionalInformation[key].value,
        };
      }

      return {
        id: needInformationKey,
        value: reasonAdditionalInformation[key],
      };
    });

  return [...neededInformationsDidactics, ...neededInformationsFormated];
};

export const neededInformationFiltered = (
  neededInformations?: AdditionalInformation[],
  isMultiProduct?: boolean,
): AdditionalInformation[] | undefined => {
  if (!neededInformations || !neededInformations?.length) {
    return undefined;
  }

  if (isMultiProduct === true) {
    return neededInformations
      .filter((item) => !isFileType(item))
      .filter((item) => item.relatedTo === RELATED_TO.CLAIM);
  }

  return neededInformations.filter((item) => !isFileType(item));
};
